/**
 * PendingStatus component for the Indigo design system.
 *
 * An inline "will happen" marker: a dashed circle followed by a short label.
 * Use it for an outcome that is queued but has not happened yet — something
 * that will be created, sent or scheduled once the person confirms. The
 * dashed ring reads as "not yet", in contrast to the filled icons used for
 * states that have already been reached.
 *
 * The label is free content, so it can carry a link to the target:
 *
 * @example
 * ```tsx
 * <PendingStatus>To be created</PendingStatus>
 * <PendingStatus>
 *   Reply to be sent to <a href={ticketUrl}>#12345</a>
 * </PendingStatus>
 * ```
 */
import { clsx } from "clsx";
import type { ReactNode } from "react";
import { Icon } from "../Icons";
import "./PendingStatus.css";

export interface PendingStatusProps {
  /** Label describing what is pending. May include a link. */
  children: ReactNode;

  /** Additional CSS classes applied to the root element. */
  className?: string;

  /** Test id applied to the root element. */
  "data-testid"?: string;
}

export const PendingStatus = ({
  children,
  className,
  "data-testid": dataTestId,
}: PendingStatusProps) => (
  <span
    className={clsx("indigo-pending-status", className)}
    data-testid={dataTestId}
  >
    <Icon
      name="circle-dashed"
      size="s"
      className="indigo-pending-status__icon"
    />
    <span className="indigo-pending-status__label">{children}</span>
  </span>
);

PendingStatus.displayName = "PendingStatus";
